import { useState, useRef, useEffect, useCallback, memo } from 'react';
import { Send, Smile, Image, Link, User } from 'lucide-react';
import { AIMWindow } from './AIMWindow';
import { AIMButton } from './AIMButton';
import { useConversationMessages } from '@/hooks/useConversationMessages';
import { useDMContext } from '@/hooks/useDMContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuthor } from '@/hooks/useAuthor';
import { genUserName } from '@/lib/genUserName';
import { MESSAGE_PROTOCOL } from '@/lib/dmConstants';
import { cn } from '@/lib/utils';
import type { NostrEvent } from '@nostrify/nostrify';

interface IMWindowProps {
  pubkey: string;
  onClose: () => void;
}

const MessageComponent = memo(({
  message,
  isFromMe,
  myName,
  theirName,
}: {
  message: {
    id: string;
    pubkey: string;
    decryptedContent?: string;
    error?: string;
    created_at: number;
  };
  isFromMe: boolean;
  myName: string;
  theirName: string;
}) => {
  const senderName = isFromMe ? myName : theirName;
  const time = new Date(message.created_at * 1000).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <div className="aim-message">
      <span className={cn('aim-message-sender', isFromMe ? 'you' : 'them')}>
        {senderName}
      </span>
      <span className="text-gray-500 dark:text-gray-400 text-[11px]"> ({time})</span>
      <span>: </span>
      {message.error ? (
        <span className="italic text-gray-500">[encrypted message]</span>
      ) : (
        <span>{message.decryptedContent}</span>
      )}
    </div>
  );
});

MessageComponent.displayName = 'Message';

export function IMWindow({ pubkey, onClose }: IMWindowProps) {
  const { user, metadata: myMetadata } = useCurrentUser();
  const { sendMessage } = useDMContext();
  const { messages, hasMoreMessages, loadEarlierMessages } = useConversationMessages(pubkey);

  const author = useAuthor(pubkey);
  const theirMetadata = author.data?.metadata;

  const myName = myMetadata?.name || (user ? genUserName(user.pubkey) : 'Me');
  const theirName = theirMetadata?.name || genUserName(pubkey);

  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);

  const chatRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages.length]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = useCallback(async () => {
    if (!messageText.trim() || !user) return;

    setIsSending(true);
    try {
      await sendMessage({
        recipientPubkey: pubkey,
        content: messageText.trim(),
        protocol: MESSAGE_PROTOCOL.NIP17,
      });
      setMessageText('');
      inputRef.current?.focus();
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  }, [messageText, pubkey, user, sendMessage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  return (
    <AIMWindow
      title={`${theirName} - Instant Message`}
      icon={
        theirMetadata?.picture ? (
          <img src={theirMetadata.picture} className="w-4 h-4 object-cover" alt="" />
        ) : (
          <User className="w-4 h-4" />
        )
      }
      onClose={onClose}
      className="w-[450px] h-[400px] min-w-[350px] min-h-[300px]"
    >
      {/* Menu Bar */}
      <div className="bg-[hsl(var(--muted))] border-b border-[hsl(var(--border))] px-1 py-0.5 text-xs flex gap-3">
        <button className="hover:underline">File</button>
        <button className="hover:underline">Edit</button>
        <button className="hover:underline">Insert</button>
        <button className="hover:underline">People</button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col p-1 gap-1 overflow-hidden">
        {/* Buddy Info Bar */}
        <div className="flex items-center gap-2 bg-[hsl(var(--card))] p-1 border border-[hsl(var(--border))]">
          <div className="w-12 h-12 bg-[hsl(var(--muted))] border-2 border-[hsl(var(--border))] flex items-center justify-center flex-shrink-0">
            {theirMetadata?.picture ? (
              <img
                src={theirMetadata.picture}
                alt={theirName}
                className="w-full h-full object-cover"
              />
            ) : (
              <User className="w-6 h-6 text-[hsl(var(--muted-foreground))]" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold truncate">{theirName}</div>
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] truncate">
              {theirMetadata?.about || 'No status message'}
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div
          ref={chatRef}
          className="aim-chat-area flex-1 min-h-0"
        >
          {hasMoreMessages && (
            <div className="text-center mb-2">
              <button
                onClick={loadEarlierMessages}
                className="text-[10px] text-blue-600 hover:underline"
              >
                Load earlier messages...
              </button>
            </div>
          )}
          {messages.length === 0 ? (
            <div className="text-center text-gray-500 text-xs py-4">
              <p>You are now chatting with <strong>{theirName}</strong></p>
              <p className="mt-1 text-[10px]">Say hello!</p>
            </div>
          ) : (
            messages.map((message) => (
              <MessageComponent
                key={message.id}
                message={message}
                isFromMe={message.pubkey === user?.pubkey}
                myName={myName}
                theirName={theirName}
              />
            ))
          )}
        </div>

        {/* Input Area */}
        <div className="flex flex-col gap-1">
          {/* Formatting Toolbar */}
          <div className="flex items-center gap-0.5 bg-[hsl(var(--muted))] p-0.5 border border-[hsl(var(--border))]">
            <button className="aim-window-button w-5 h-5" title="Font">
              <span className="text-[10px] font-bold">A</span>
            </button>
            <button className="aim-window-button w-5 h-5" title="Emoticons">
              <Smile className="w-3 h-3" />
            </button>
            <button className="aim-window-button w-5 h-5" title="Insert Image">
              <Image className="w-3 h-3" />
            </button>
            <button className="aim-window-button w-5 h-5" title="Insert Link">
              <Link className="w-3 h-3" />
            </button>
          </div>

          {/* Text Input */}
          <textarea
            ref={inputRef}
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type your message here..."
            className="aim-input min-h-[60px] max-h-[100px] resize-none text-[13px]"
            style={{ fontFamily: "'Times New Roman', Georgia, serif" }}
            disabled={isSending}
          />

          {/* Send Area */}
          <div className="flex items-center justify-between">
            <div className="text-[10px] text-[hsl(var(--muted-foreground))]">
              Press Enter to send, Shift+Enter for new line
            </div>
            <div className="flex gap-1">
              <AIMButton onClick={onClose}>
                Close
              </AIMButton>
              <AIMButton
                variant="primary"
                onClick={handleSend}
                disabled={!messageText.trim() || isSending}
              >
                {isSending ? 'Sending...' : 'Send'}
              </AIMButton>
            </div>
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div className="bg-[hsl(var(--muted))] border-t border-[hsl(var(--border))] px-2 py-0.5 text-[10px] text-[hsl(var(--muted-foreground))]">
        {isSending ? 'Sending message...' : 'Direct connection established'}
      </div>
    </AIMWindow>
  );
}
